import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { ChevronDown, Lightbulb } from 'lucide-react';
import { Button, Card, CardContent } from '@/features/common/ui';
import { cn } from '@/lib/cn';

export interface Advice {
  id: string;
  rule: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  figures: Array<{ label: string; value: string }>;
  href: string;
  action: string;
  amount: string;
}

/** How many cards before "Show n more": a phone's first screen, not a feed. */
const SHOWN = 5;

interface Dismissal {
  cardId: string;
  amount: string;
  /** Snoozed until this date; null for a dismissal. */
  snoozedUntil: string | null;
}

/**
 * The advice cards: what the books say to do, most urgent first.
 *
 * ── EVERY CARD SHOWS ITS WORKING ──
 * A headline, one line of why, and "See the numbers" - the figures the
 * card was built from - so nothing on it has to be taken on trust. The one
 * button goes to the screen that deals with it; nothing here acts.
 *
 * ── QUIET ON REQUEST ──
 * Dismiss hides a card until its amount moves by a fifth (a new late
 * invoice is a new conversation); Snooze hides it for two weeks. Both are
 * kept per person on the server (`cfo_dismissals`), so a card put away on
 * the phone stays away on the laptop, and a partner still sees it.
 */
export function AdviceStrip({ orgId, advice }: { orgId: string; advice: Advice[] }) {
  const queryClient = useQueryClient();
  const [all, setAll] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const key = ['cfo-dismissals', orgId];
  const stored = useQuery({ queryKey: key, queryFn: () => api.get<{ items: Dismissal[] }>('/cfo/dismissals') });
  const dismissed = new Map((stored.data?.items ?? []).map((d) => [d.cardId, d]));
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
  };
  const put = useMutation({
    mutationFn: (input: { card: Advice; snooze: boolean }) => api.put('/cfo/dismissals', { cardId: input.card.id, amount: input.card.amount, snooze: input.snooze }),
    onSuccess: refresh,
  });
  const restoreMany = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.delete(`/cfo/dismissals?cardId=${encodeURIComponent(id)}`);
    },
    onSuccess: refresh,
  });

  const isHidden = (card: Advice) => {
    const d = dismissed.get(card.id);
    if (!d) return false;
    if (d.snoozedUntil) return d.snoozedUntil >= today;
    const before = Math.abs(Number(d.amount));
    const now = Math.abs(Number(card.amount));
    return before === 0 ? now === 0 : Math.abs(now - before) / before <= 0.2;
  };
  const visible = advice.filter((card) => !isHidden(card));
  const hiddenIds = advice.filter(isHidden).map((card) => card.id);
  const hidden = hiddenIds.length;
  const shown = all ? visible : visible.slice(0, SHOWN);

  const remember = (card: Advice, snooze: boolean) => put.mutate({ card, snooze });
  const restore = () => restoreMany.mutate(hiddenIds);

  if (advice.length === 0) {
    return (
      <Card data-cfo-advice-empty>
        <CardContent className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Lightbulb className="size-4" aria-hidden />
          Nothing needs doing from what the books show today.
        </CardContent>
      </Card>
    );
  }

  return (
    <section aria-labelledby="cfo-advice" className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="cfo-advice" className="flex items-center gap-2 text-sm font-semibold">
          <Lightbulb className="size-4 text-muted-foreground" aria-hidden />
          What to do next
        </h2>
        {hidden > 0 ? (
          <button type="button" onClick={restore} className="text-xs text-primary hover:underline touch:min-h-11">
            Show {hidden} hidden
          </button>
        ) : null}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {shown.map((card) => (
          <AdviceCard key={card.id} card={card} onDismiss={() => remember(card, false)} onSnooze={() => remember(card, true)} />
        ))}
      </div>
      {!all && visible.length > SHOWN ? (
        <Button variant="ghost" size="sm" onClick={() => setAll(true)}>
          Show {visible.length - SHOWN} more
        </Button>
      ) : null}
    </section>
  );
}

const SEVERITY: Record<Advice['severity'], { label: string; dot: string }> = {
  high: { label: 'Act now', dot: 'bg-destructive' },
  medium: { label: 'This week', dot: 'bg-warning' },
  low: { label: 'Worth a look', dot: 'bg-muted-foreground' },
};

function AdviceCard({ card, onDismiss, onSnooze }: { card: Advice; onDismiss: () => void; onSnooze: () => void }) {
  const [open, setOpen] = useState(false);
  const severity = SEVERITY[card.severity];
  return (
    <Card data-cfo-advice={card.rule}>
      <CardContent className="space-y-2 pt-4">
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={cn('inline-block size-2 rounded-full', severity.dot)} aria-hidden />
          {severity.label}
        </p>
        <h3 className="text-base font-semibold leading-snug">{card.title}</h3>
        <p className="text-sm text-muted-foreground">{card.detail}</p>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline touch:min-h-11"
        >
          See the numbers
          <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
        {open ? (
          <dl className="space-y-1 rounded-md bg-muted/50 px-3 py-2 text-xs">
            {card.figures.map((f) => (
              <div key={f.label} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="font-medium tabular-nums">{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button asChild size="sm">
            <Link to={card.href}>{card.action}</Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={onSnooze}>
            Snooze 2 weeks
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
